import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { NavbarComponent } from '../../../shared/ui/navbar/navbar.component';
import { SidebarComponent } from '../../../shared/ui/sidebar/sidebar.component';

import { EstadoSidebarService } from '../../services/estado-sidebar.service';
import { TituloNavbarService } from '../../services/titulo-navbar.service';
import { TemaService } from '../../services/tema.service';

@Component({
  selector: 'app-layout-modulos',
  standalone: true,
  imports: [CommonModule, RouterModule, SidebarComponent, NavbarComponent],
  templateUrl: './layout-modulos.component.html',
  styleUrls: ['./layout-modulos.component.scss'],
})
export class LayoutModulosComponent implements OnInit, OnDestroy {
  isSidebarExpanded: boolean = true;
  tituloAtual: string = 'Meus módulos';
  isDarkMode: boolean = false;

  private subSidebar?: Subscription;
  private subTema?: Subscription;
  private subTitulo?: Subscription;
  private subRouter?: Subscription;

  constructor(
    public router: Router,
    private estadoSidebar: EstadoSidebarService,
    private tituloNavbarService: TituloNavbarService,
    private temaService: TemaService,
  ) {}

  ngOnInit(): void {
    this.subSidebar = this.estadoSidebar.expandida$.subscribe((v) => {
      this.isSidebarExpanded = v;
    });

    this.isDarkMode = this.temaService.estaEscuro();
    this.subTema = this.temaService.modoEscuro$.subscribe((modoEscuro) => {
      this.isDarkMode = modoEscuro;
    });

    this.subTitulo = this.tituloNavbarService.titulo$.subscribe((titulo) => {
      this.tituloAtual = titulo;
    });

    this.subRouter = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.aplicarTituloFallback();
      });

    this.aplicarTituloFallback();
  }

  ngOnDestroy(): void {
    this.subSidebar?.unsubscribe();
    this.subTema?.unsubscribe();
    this.subTitulo?.unsubscribe();
    this.subRouter?.unsubscribe();
  }

  onToggleSidebar(): void {
    this.estadoSidebar.alternar();
  }

  atualizarTitulo(novoTitulo: string): void {
    this.tituloNavbarService.setTitulo(novoTitulo);
  }

  private aplicarTituloFallback(): void {
    const pathAtual = this.router.url.split('?')[0].split('#')[0];

    if (pathAtual === '/modulos') {
      this.tituloNavbarService.setTitulo('Meus módulos');
      return;
    }

    // Tentar buscar título do menu no localStorage
    const tituloDoMenu = this.buscarTituloNoMenu(pathAtual);
    if (tituloDoMenu) {
      console.log(' Aplicando título do menu:', tituloDoMenu);
      this.tituloNavbarService.setTitulo(tituloDoMenu);
      this.tituloNavbarService.setTituloParaRota(pathAtual, tituloDoMenu);
      return;
    }

    // Tentar obter título do data da rota ativa
    let routeSnapshot = this.router.routerState.root;
    while (routeSnapshot.firstChild) {
      routeSnapshot = routeSnapshot.firstChild;
    }

    const tituloRota = routeSnapshot.snapshot?.data?.['title'] as
      | string
      | undefined;
    if (tituloRota) {
      // Se tem título configurado, montar breadcrumb com contexto
      const partes = pathAtual.split('/').filter(Boolean);
      let tituloCompleto = tituloRota;

      if (partes.length > 1 && partes[0] !== 'modulos') {
        const moduloPrincipal = partes[0]
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase());
        tituloCompleto = `${moduloPrincipal} / ${tituloRota}`;
      }

      this.tituloNavbarService.setTitulo(tituloCompleto);
      this.tituloNavbarService.setTituloParaRota(pathAtual, tituloCompleto);
      return;
    }

    const tituloSalvo = this.tituloNavbarService.getTituloParaRota(pathAtual);
    if (tituloSalvo) {
      this.tituloNavbarService.setTitulo(tituloSalvo);
      return;
    }

    const partes = pathAtual.split('/').filter(Boolean);
    if (!partes.length) {
      this.tituloNavbarService.setTitulo('Início');
      this.tituloNavbarService.setTituloParaRota(pathAtual, 'Início');
      return;
    }

    const titulo = partes
      .slice(0, 2)
      .map((p) =>
        (p || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      )
      .join(' / ');

    this.tituloNavbarService.setTitulo(titulo);
    this.tituloNavbarService.setTituloParaRota(pathAtual, titulo);
    return;
  }

  private buscarTituloNoMenu(path: string): string | null {
    try {
      const menuStr = localStorage.getItem('menu_sidebar');
      if (!menuStr) {
        console.log(' Nenhum menu encontrado no localStorage');
        return null;
      }

      const menu = JSON.parse(menuStr);
      if (!Array.isArray(menu)) return null;

      // Normalizar path e criar variantes
      const pathNorm = path.replace(/\/+$/, '') || '/';
      console.log(' Buscando título para path:', pathNorm);

      // Mapa de aliases (frontend -> backend)
      const aliases: Record<string, string> = {
        '/empresas/pre_cadastro': '/empresas/pre_cadastro_empresa/listar',
        '/empresas/pre_cadastro_empresa/listar': '/empresas/pre_cadastro',
        '/med/listar-por-empresa':
          '/med/invoice_pix_med_arquivo/listar-por-empresa',
        '/med/invoice_pix_med_arquivo/listar-por-empresa':
          '/med/invoice_pix_med_arquivo/listar-por-empresa',
        '/med/invoice_pix_med/listar-arquivos':
          '/med/invoice_pix_med/listar-arquivos',
        '/med/invoice_pix_med/criar': '/med/invoice_pix_med/criar',
        '/med/invoice_pix_med_arquivo/atualizar':
          '/med/invoice_pix_med_arquivo/atualizar',
      };

      // Tentar buscar com o path original e com os aliases
      const pathsParaBuscar = [pathNorm, aliases[pathNorm]].filter(Boolean);

      for (const pathBusca of pathsParaBuscar) {
        console.log('   Tentando path:', pathBusca);

        for (const modulo of menu) {
          const tituloModulo = modulo?.titulo || '';

          // Verificar se algum subitem corresponde ao path
          const subItems = modulo?.subItems || [];
          for (const subItem of subItems) {
            const subPath = (subItem?.path || '').replace(/\/+$/, '') || '/';

            console.log(
              `    Comparando: "${subPath}" === "${pathBusca}"`,
              subPath === pathBusca,
            );

            if (subPath === pathBusca) {
              // Encontrou! Montar título no formato "Módulo / SubItem"
              const tituloSub = subItem?.titulo || '';
              const resultado =
                tituloModulo && tituloSub
                  ? `${tituloModulo} / ${tituloSub}`
                  : tituloSub || tituloModulo;
              console.log(' Título encontrado:', resultado);
              return resultado;
            }
          }

          // Verificar se o path do módulo corresponde
          const moduloPath = (modulo?.path || '').replace(/\/+$/, '') || '/';
          if (moduloPath === pathBusca) {
            console.log(' Título do módulo encontrado:', tituloModulo);
            return tituloModulo;
          }
        }
      }

      console.log(' Nenhum título encontrado no menu');
      return null;
    } catch (error) {
      console.error('Erro ao buscar título no menu:', error);
      return null;
    }
  }
}
